import * as React from "react";
import DateRange, {RangeToTimes} from "../../components/DateRange";
import Loading from "../../components/loading";
import {
    Alert,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    Link as MuiLink,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableSortLabel,
    TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import TableHeaderRow from "../../components/TableHeaderRow";
import StripedTableRow from "../../components/StripedTableRow";
import TableFooterRow from "../../components/TableFooterRow";
import MultiSelect from "../../components/multiSelect/multiSelect";
import useDebounce from "../../lib/UseDebounce";
import {useAuth} from "../../providers/auth";
import {PermissionType} from "../../objects/UserRead";
import {toast} from "react-toastify";
import moment from "moment";
import {fetchCompaniesActive} from "./campaignsService";
import {downloadHpCampaignsCsv, phpField, searchHpCampaignsReport, updateHpCampaignField} from "./hpCampaignsService";

const ASC_DEFAULT: Record<string, boolean> = {
    company_name: true,
    hp_campaign_id: true,
    broker_name: true,
    school_name: true,
    app_id: true,
    campaign_grade: true,
};

const EDIT_FIELDS = ["appId", "campaignGrade", "hoursOldMin", "hoursOldMax", "costPerLead", "dailyCap", "monthlyCap"];

function money(n: number) {
    return "$" + Number(n || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

function num(n: number) {
    return Number(n || 0).toLocaleString(undefined, {maximumFractionDigits: 0});
}

function toNum(s: string): number | undefined {
    if (s === "" || s == null) {
        return undefined;
    }
    const n = Number(s);
    return Number.isNaN(n) ? undefined : n;
}

function toInt(s: string): number | undefined {
    if (!s || !/^\d+$/.test(String(s).trim())) {
        return undefined;
    }
    return Number(s.trim());
}

function RangeFilter(params: {
    open: boolean;
    setOpen: (v: boolean) => void;
    start: string;
    end: string;
    setStart: (v: string) => void;
    setEnd: (v: string) => void;
}) {
    const active = !!(params.start || params.end);
    return (
        <Box sx={{display: "flex", alignItems: "center", gap: 0.25, whiteSpace: "nowrap"}}>
            <FilterListIcon
                sx={{cursor: "pointer", fontSize: 16, color: active ? "warning.main" : "action.active"}}
                onClick={() => params.setOpen(!params.open)}
            />
            {params.open && (
                <>
                    <TextField variant="standard" placeholder="Start" value={params.start} onChange={(e) => params.setStart(e.target.value)} sx={{width: 52}} inputProps={{style: {fontSize: 12}}}/>
                    <Box sx={{px: 0.25}}>-</Box>
                    <TextField variant="standard" placeholder="End" value={params.end} onChange={(e) => params.setEnd(e.target.value)} sx={{width: 52}} inputProps={{style: {fontSize: 12}}}/>
                </>
            )}
        </Box>
    );
}

export default function HpCampaignsPage() {
    const auth = useAuth();
    const isAdmin = !!auth.user?.permissions?.includes(PermissionType.Admin);
    const canModify = !!auth.user?.permissions?.includes(PermissionType.HPCampaignsModify);
    const canPm = !!auth.user?.permissions?.includes(PermissionType.HPCampaignsPM);
    const showAll = !!auth.user?.permissions?.includes(PermissionType.HPCampaignsShowAll);

    const [dateRange, setDateRange] = React.useState([] as any[]);
    const [limit, setLimit] = React.useState(100);
    const [active, setActive] = React.useState(true);
    const [selectedCompanies, setSelectedCompanies] = React.useState([] as any[]);
    const [companies, setCompanies] = React.useState([] as any[]);
    const [loading, setLoading] = React.useState(true);
    const [rows, setRows] = React.useState([] as any[]);
    const [editAll, setEditAll] = React.useState(false);
    const bulkRef = React.useRef<Record<number, Record<string, any>>>({});
    const snapshotRef = React.useRef<Record<number, Record<string, any>>>({});
    const [sort, setSort] = React.useState({column: "hp_campaign_id", asc: false});

    const [hpCampaignId, setHpCampaignId] = React.useState("");
    const [brokerName, setBrokerName] = React.useState("");
    const [schoolName, setSchoolName] = React.useState("");
    const [appId, setAppId] = React.useState("");
    const [campaignGrade, setCampaignGrade] = React.useState("");
    const [hpEventId, setHpEventId] = React.useState("");
    const [hpCampusSearchId, setHpCampusSearchId] = React.useState("");

    const [hoursOldMinStart, setHoursOldMinStart] = React.useState("");
    const [hoursOldMinEnd, setHoursOldMinEnd] = React.useState("");
    const [hoursOldMaxStart, setHoursOldMaxStart] = React.useState("");
    const [hoursOldMaxEnd, setHoursOldMaxEnd] = React.useState("");
    const [cplStart, setCplStart] = React.useState("");
    const [cplEnd, setCplEnd] = React.useState("");
    const [dailyStart, setDailyStart] = React.useState("");
    const [dailyEnd, setDailyEnd] = React.useState("");
    const [dailyCapStart, setDailyCapStart] = React.useState("");
    const [dailyCapEnd, setDailyCapEnd] = React.useState("");
    const [monthlyStart, setMonthlyStart] = React.useState("");
    const [monthlyEnd, setMonthlyEnd] = React.useState("");
    const [monthlyCapStart, setMonthlyCapStart] = React.useState("");
    const [monthlyCapEnd, setMonthlyCapEnd] = React.useState("");
    const [rangeGoodStart, setRangeGoodStart] = React.useState("");
    const [rangeGoodEnd, setRangeGoodEnd] = React.useState("");
    const [rangeUnknownStart, setRangeUnknownStart] = React.useState("");
    const [rangeUnknownEnd, setRangeUnknownEnd] = React.useState("");
    const [rangeBadStart, setRangeBadStart] = React.useState("");
    const [rangeBadEnd, setRangeBadEnd] = React.useState("");

    const [openHoursMin, setOpenHoursMin] = React.useState(false);
    const [openHoursMax, setOpenHoursMax] = React.useState(false);
    const [openCpl, setOpenCpl] = React.useState(false);
    const [openDaily, setOpenDaily] = React.useState(false);
    const [openDailyCap, setOpenDailyCap] = React.useState(false);
    const [openMonthly, setOpenMonthly] = React.useState(false);
    const [openMonthlyCap, setOpenMonthlyCap] = React.useState(false);
    const [openGood, setOpenGood] = React.useState(false);
    const [openUnknown, setOpenUnknown] = React.useState(false);
    const [openBad, setOpenBad] = React.useState(false);

    React.useEffect(() => {
        document.title = "HP Campaigns | DN";
        setDateRange(RangeToTimes("Month to Date"));
        if (isAdmin) {
            fetchCompaniesActive().then((list: any[]) => {
                setCompanies(list.map((c: any) => ({id: c.id, value: c.name})));
            });
        }
    }, []);

    const buildInput = (date = dateRange) => ({
        startDate: moment(date[0]).format("YYYY-MM-DD HH:mm:ss"),
        endDate: moment(date[1]).format("YYYY-MM-DD HH:mm:ss"),
        limit,
        active,
        hpCampaignId: hpCampaignId || undefined,
        brokerName: brokerName || undefined,
        schoolName: schoolName || undefined,
        appId: appId || undefined,
        campaignGrade: campaignGrade || undefined,
        hoursOldMinStart: toNum(hoursOldMinStart),
        hoursOldMinEnd: toNum(hoursOldMinEnd),
        hoursOldMaxStart: toNum(hoursOldMaxStart),
        hoursOldMaxEnd: toNum(hoursOldMaxEnd),
        companyIds: selectedCompanies.length ? selectedCompanies.map((c: any) => c.id) : undefined,
        hpEventId: toInt(hpEventId),
        hpCampusSearchId: toInt(hpCampusSearchId),
        cplStart: toNum(cplStart),
        cplEnd: toNum(cplEnd),
        dailyStart: toNum(dailyStart),
        dailyEnd: toNum(dailyEnd),
        dailyCapStart: toNum(dailyCapStart),
        dailyCapEnd: toNum(dailyCapEnd),
        monthlyStart: toNum(monthlyStart),
        monthlyEnd: toNum(monthlyEnd),
        monthlyCapStart: toNum(monthlyCapStart),
        monthlyCapEnd: toNum(monthlyCapEnd),
        rangeGoodStart: toNum(rangeGoodStart),
        rangeGoodEnd: toNum(rangeGoodEnd),
        rangeUnknownStart: toNum(rangeUnknownStart),
        rangeUnknownEnd: toNum(rangeUnknownEnd),
        rangeBadStart: toNum(rangeBadStart),
        rangeBadEnd: toNum(rangeBadEnd),
        sortColumn: sort.column,
        sortAsc: sort.asc,
    });

    const load = async (date = dateRange) => {
        if (!date?.length) {
            return;
        }
        setLoading(true);
        setEditAll(false);
        bulkRef.current = {};
        snapshotRef.current = {};
        const data = await searchHpCampaignsReport(buildInput(date));
        setRows((data || []).map((r: any) => ({...r, edit: {}, old: {}})));
        setLoading(false);
    };

    React.useEffect(() => {
        if (dateRange.length) {
            load(dateRange);
        }
    }, [dateRange, limit, active, selectedCompanies, sort, campaignGrade,
        hoursOldMinStart, hoursOldMinEnd, hoursOldMaxStart, hoursOldMaxEnd,
        cplStart, cplEnd, dailyStart, dailyEnd, dailyCapStart, dailyCapEnd,
        monthlyStart, monthlyEnd, monthlyCapStart, monthlyCapEnd,
        rangeGoodStart, rangeGoodEnd, rangeUnknownStart, rangeUnknownEnd, rangeBadStart, rangeBadEnd]);

    useDebounce(() => {
        if (dateRange.length) {
            load(dateRange);
        }
    }, [hpCampaignId, brokerName, schoolName, appId, hpEventId, hpCampusSearchId], 400);

    const searchCompanies = async (search: string, selected: any[]) => {
        const ids = selected.map((x: any) => x.id);
        const q = search.toLowerCase();
        return companies.filter((c: any) => !ids.includes(c.id) && (!q || c.value.toLowerCase().includes(q)));
    };

    const doSort = (column: string) => {
        if (sort.column === column) {
            setSort({column, asc: !sort.asc});
        } else {
            setSort({column, asc: !!ASC_DEFAULT[column]});
        }
    };

    const startEdit = (id: number, field: string) => {
        if (!canModify) {
            return;
        }
        setRows((prev) => prev.map((r) => {
            if (r.hpCampaignId !== id || r.edit?.[field]) {
                return r;
            }
            return {...r, edit: {...r.edit, [field]: true}, old: {...r.old, [field]: r[field]}};
        }));
    };

    const cancelEdit = (id: number, field: string) => {
        setRows((prev) => prev.map((r) => {
            if (r.hpCampaignId !== id) {
                return r;
            }
            return {...r, [field]: r.old?.[field], edit: {...r.edit, [field]: false}};
        }));
    };

    const setField = (id: number, field: string, value: any) => {
        setRows((prev) => prev.map((r) => r.hpCampaignId === id ? {...r, [field]: value} : r));
    };

    const saveField = async (row: any, field: string, value?: any) => {
        const next = value === undefined ? row[field] : value;
        const ok = await updateHpCampaignField(row.hpCampaignId, phpField(field), String(next ?? ""));
        if (!ok) {
            toast.error("Unable to update, please try again.");
            return false;
        }
        setRows((prev) => prev.map((r) => r.hpCampaignId === row.hpCampaignId
            ? {...r, [field]: next, edit: {...r.edit, [field]: false}}
            : r));
        return true;
    };

    const editFields = showAll
        ? EDIT_FIELDS
        : ["appId", "costPerLead", "dailyCap", "monthlyCap"];

    const openAll = () => {
        const snap: Record<number, Record<string, any>> = {};
        const bulk: Record<number, Record<string, any>> = {};
        for (const r of rows) {
            snap[r.hpCampaignId] = {};
            bulk[r.hpCampaignId] = {};
            for (const f of editFields) {
                snap[r.hpCampaignId][f] = r[f];
                bulk[r.hpCampaignId][f] = r[f];
            }
        }
        snapshotRef.current = snap;
        bulkRef.current = bulk;
        setEditAll(true);
    };

    const cancelAll = () => {
        bulkRef.current = {};
        snapshotRef.current = {};
        setEditAll(false);
    };

    const approveAll = async () => {
        if (!window.confirm("Are you sure you want to update all values?")) {
            return;
        }
        for (const row of rows) {
            for (const f of editFields) {
                const next = bulkRef.current[row.hpCampaignId]?.[f];
                const orig = snapshotRef.current[row.hpCampaignId]?.[f];
                if (next === undefined || String(next) === String(orig)) {
                    continue;
                }
                await saveField(row, f, next);
            }
        }
        bulkRef.current = {};
        snapshotRef.current = {};
        setEditAll(false);
    };

    const totals = React.useMemo(() => rows.reduce((a, r) => ({
        dailySent: a.dailySent + (r.dailySent || 0),
        dailyCap: a.dailyCap + (r.dailyCap || 0),
        monthlySent: a.monthlySent + (r.monthlySent || 0),
        monthlyCap: a.monthlyCap + (r.monthlyCap || 0),
        rangeGood: a.rangeGood + (r.rangeGood || 0),
        rangeUnknown: a.rangeUnknown + (r.rangeUnknown || 0),
        rangeBad: a.rangeBad + (r.rangeBad || 0),
    }), {dailySent: 0, dailyCap: 0, monthlySent: 0, monthlyCap: 0, rangeGood: 0, rangeUnknown: 0, rangeBad: 0}), [rows]);

    const thFilter = {fontSize: 12, minWidth: 70, padding: "4px"};
    const sortLabel = (column: string, label: string) => (
        <TableSortLabel
            active={sort.column === column}
            direction={sort.column === column && sort.asc ? "asc" : "desc"}
            onClick={() => doSort(column)}
        >
            {label}
        </TableSortLabel>
    );

    const editable = (row: any, field: string, display: React.ReactNode, type = "text") => {
        const editing = canModify && (editAll || row.edit?.[field]);
        if (!canModify) {
            return display;
        }
        if (!editing) {
            return (
                <Box onDoubleClick={() => startEdit(row.hpCampaignId, field)} sx={{minHeight: 17, cursor: "cell"}}>
                    {display}
                </Box>
            );
        }
        const inputStyle: React.CSSProperties = {width: "100%", fontSize: 12, boxSizing: "border-box"};
        if (editAll) {
            return (
                <input
                    defaultValue={row[field] ?? ""}
                    type={type === "number" ? "number" : "text"}
                    maxLength={field === "campaignGrade" ? 1 : undefined}
                    style={inputStyle}
                    onChange={(e) => {
                        if (!bulkRef.current[row.hpCampaignId]) {
                            bulkRef.current[row.hpCampaignId] = {};
                        }
                        bulkRef.current[row.hpCampaignId][field] = e.target.value;
                    }}
                />
            );
        }
        return (
            <Box>
                <input
                    value={row[field] ?? ""}
                    type={type === "number" ? "number" : "text"}
                    maxLength={field === "campaignGrade" ? 1 : undefined}
                    style={inputStyle}
                    onChange={(e) => setField(row.hpCampaignId, field, e.target.value)}
                />
                <Box sx={{pt: 0.5, display: "flex", gap: 1, fontSize: 12}}>
                    <MuiLink component="button" onClick={() => saveField(row, field)} sx={{display: "flex", alignItems: "center"}}>
                        <CheckIcon sx={{fontSize: 14, color: "success.main"}}/> Update
                    </MuiLink>
                    <MuiLink component="button" onClick={() => cancelEdit(row.hpCampaignId, field)} sx={{display: "flex", alignItems: "center"}}>
                        <CloseIcon sx={{fontSize: 14, color: "error.main"}}/> Cancel
                    </MuiLink>
                </Box>
            </Box>
        );
    };

    return (
        <>
            <h2>HP Campaigns</h2>
            <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1, mb: 1}}>
                <FormControl size="small" sx={{minWidth: 80}}>
                    <InputLabel>Limit</InputLabel>
                    <Select value={limit} label="Limit" onChange={(e) => setLimit(Number(e.target.value))}>
                        {[100, 500, 1000, 2000, 10000].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </Select>
                </FormControl>
                <DateRange showTime={true} dateRange={dateRange} setDateRange={setDateRange} style={{marginRight: 8}}/>
                <FormControlLabel
                    control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} size="small"/>}
                    label="Active"
                />
                {isAdmin && (
                    <Box sx={{width: 180}}>
                        <MultiSelect searchFunction={searchCompanies} selectedItems={selectedCompanies} setSelectedItems={setSelectedCompanies} placeholder="Comp"/>
                    </Box>
                )}
                <Button variant="contained" color="success" onClick={() => load()}>Refresh</Button>
                <IconButton onClick={() => downloadHpCampaignsCsv(rows, showAll)} title="Download" disabled={!rows.length}>
                    <FileDownloadIcon/>
                </IconButton>
            </Box>
            {canModify && (
                <Box sx={{display: "flex", gap: 1, mb: 1}}>
                    {!editAll && <Button variant="contained" onClick={openAll} disabled={!rows.length}>Open All</Button>}
                    {editAll && <Button variant="contained" color="success" onClick={approveAll}>Update All</Button>}
                    {editAll && <Button variant="contained" color="error" onClick={cancelAll}>Cancel All</Button>}
                </Box>
            )}

            <TableContainer component={Paper} sx={{maxHeight: "calc(100vh - 240px)", maxWidth: "calc(100vw - 10px)"}}>
                <Table size="small" stickyHeader sx={{fontSize: 12}}>
                    <TableHead>
                        <TableHeaderRow>
                            <TableCell sx={thFilter}/>
                            <TableCell sx={thFilter}/>
                            <TableCell sx={thFilter}>
                                <TextField variant="standard" placeholder="Search Campaign Id" value={hpCampaignId} onChange={(e) => setHpCampaignId(e.target.value)} inputProps={{style: {fontSize: 12}}}/>
                            </TableCell>
                            <TableCell sx={thFilter}>
                                <TextField variant="standard" placeholder="Search Broker" value={brokerName} onChange={(e) => setBrokerName(e.target.value)} inputProps={{style: {fontSize: 12}}}/>
                            </TableCell>
                            <TableCell sx={thFilter}>
                                <TextField variant="standard" placeholder="Search School" value={schoolName} onChange={(e) => setSchoolName(e.target.value)} inputProps={{style: {fontSize: 12}}}/>
                            </TableCell>
                            <TableCell sx={thFilter}>
                                <TextField variant="standard" placeholder="Search App Id" value={appId} onChange={(e) => setAppId(e.target.value)} inputProps={{style: {fontSize: 12}}}/>
                            </TableCell>
                            {showAll && (
                                <TableCell sx={thFilter}>
                                    <TextField variant="standard" placeholder="Grade" value={campaignGrade} onChange={(e) => setCampaignGrade(e.target.value)} inputProps={{style: {fontSize: 12}, maxLength: 1}}/>
                                </TableCell>
                            )}
                            {showAll && <TableCell sx={thFilter}><RangeFilter open={openHoursMin} setOpen={setOpenHoursMin} start={hoursOldMinStart} end={hoursOldMinEnd} setStart={setHoursOldMinStart} setEnd={setHoursOldMinEnd}/></TableCell>}
                            {showAll && <TableCell sx={thFilter}><RangeFilter open={openHoursMax} setOpen={setOpenHoursMax} start={hoursOldMaxStart} end={hoursOldMaxEnd} setStart={setHoursOldMaxStart} setEnd={setHoursOldMaxEnd}/></TableCell>}
                            {showAll && (
                                <TableCell sx={thFilter}>
                                    <TextField variant="standard" placeholder="Search Event Id" value={hpEventId} onChange={(e) => setHpEventId(e.target.value)} inputProps={{style: {fontSize: 12}}}/>
                                </TableCell>
                            )}
                            <TableCell sx={thFilter}>
                                <TextField variant="standard" placeholder="Search PM Id" value={hpCampusSearchId} onChange={(e) => setHpCampusSearchId(e.target.value)} inputProps={{style: {fontSize: 12}}}/>
                            </TableCell>
                            <TableCell sx={thFilter}><RangeFilter open={openCpl} setOpen={setOpenCpl} start={cplStart} end={cplEnd} setStart={setCplStart} setEnd={setCplEnd}/></TableCell>
                            <TableCell sx={thFilter}><RangeFilter open={openDaily} setOpen={setOpenDaily} start={dailyStart} end={dailyEnd} setStart={setDailyStart} setEnd={setDailyEnd}/></TableCell>
                            <TableCell sx={thFilter}><RangeFilter open={openDailyCap} setOpen={setOpenDailyCap} start={dailyCapStart} end={dailyCapEnd} setStart={setDailyCapStart} setEnd={setDailyCapEnd}/></TableCell>
                            <TableCell sx={thFilter}><RangeFilter open={openMonthly} setOpen={setOpenMonthly} start={monthlyStart} end={monthlyEnd} setStart={setMonthlyStart} setEnd={setMonthlyEnd}/></TableCell>
                            <TableCell sx={thFilter}><RangeFilter open={openMonthlyCap} setOpen={setOpenMonthlyCap} start={monthlyCapStart} end={monthlyCapEnd} setStart={setMonthlyCapStart} setEnd={setMonthlyCapEnd}/></TableCell>
                            <TableCell sx={thFilter}><RangeFilter open={openGood} setOpen={setOpenGood} start={rangeGoodStart} end={rangeGoodEnd} setStart={setRangeGoodStart} setEnd={setRangeGoodEnd}/></TableCell>
                            <TableCell sx={thFilter}><RangeFilter open={openUnknown} setOpen={setOpenUnknown} start={rangeUnknownStart} end={rangeUnknownEnd} setStart={setRangeUnknownStart} setEnd={setRangeUnknownEnd}/></TableCell>
                            <TableCell sx={thFilter}><RangeFilter open={openBad} setOpen={setOpenBad} start={rangeBadStart} end={rangeBadEnd} setStart={setRangeBadStart} setEnd={setRangeBadEnd}/></TableCell>
                        </TableHeaderRow>
                        <TableHeaderRow>
                            <TableCell>#</TableCell>
                            <TableCell>{sortLabel("company_name", "Cmp")}</TableCell>
                            <TableCell>{sortLabel("hp_campaign_id", "Campaign")}</TableCell>
                            <TableCell>{sortLabel("broker_name", "Broker")}</TableCell>
                            <TableCell>{sortLabel("school_name", "School")}</TableCell>
                            <TableCell>{sortLabel("app_id", "App Id")}</TableCell>
                            {showAll && <TableCell>{sortLabel("campaign_grade", "Grade")}</TableCell>}
                            {showAll && <TableCell>{sortLabel("hours_old_min", "H-min")}</TableCell>}
                            {showAll && <TableCell>{sortLabel("hours_old_max", "H-max")}</TableCell>}
                            {showAll && <TableCell>{sortLabel("hp_event_id", "EVENT")}</TableCell>}
                            <TableCell>{sortLabel("hp_campus_search_id", "PM")}</TableCell>
                            <TableCell align="right">{sortLabel("cost_per_lead", "CPL")}</TableCell>
                            <TableCell align="right">{sortLabel("daily_sent", "Daily")}</TableCell>
                            <TableCell align="right">{sortLabel("daily_cap", "D-cap")}</TableCell>
                            <TableCell align="right">{sortLabel("monthly_sent", "Monthly")}</TableCell>
                            <TableCell align="right">{sortLabel("monthly_cap", "M-cap")}</TableCell>
                            <TableCell align="right">{sortLabel("range_good", "Good")}</TableCell>
                            <TableCell align="right">{sortLabel("range_unknown", "Unk")}</TableCell>
                            <TableCell align="right">{sortLabel("range_bad", "Bad")}</TableCell>
                        </TableHeaderRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <StripedTableRow>
                                <TableCell colSpan={20}><Loading/></TableCell>
                            </StripedTableRow>
                        )}
                        {!loading && !rows.length && (
                            <StripedTableRow>
                                <TableCell colSpan={20}><Alert severity="warning">No results found.</Alert></TableCell>
                            </StripedTableRow>
                        )}
                        {!loading && rows.map((row, i) => (
                                <StripedTableRow key={row.hpCampaignId}>
                                    <TableCell>{i + 1}.</TableCell>
                                    <TableCell>{row.companyName}</TableCell>
                                    <TableCell sx={{bgcolor: row.campaignActive ? undefined : "#f2dede"}}>
                                        {canModify
                                            ? <MuiLink href={`/campaigns/${row.hpCampaignId}`} target="_blank" rel="noreferrer">{row.hpCampaignId}</MuiLink>
                                            : row.hpCampaignId}
                                    </TableCell>
                                    <TableCell>{row.brokerName}</TableCell>
                                    <TableCell>{row.schoolName}</TableCell>
                                    <TableCell>{editable(row, "appId", row.appId)}</TableCell>
                                    {showAll && <TableCell>{editable(row, "campaignGrade", row.campaignGrade)}</TableCell>}
                                    {showAll && <TableCell align="right">{editable(row, "hoursOldMin", row.hoursOldMin, "number")}</TableCell>}
                                    {showAll && <TableCell align="right">{editable(row, "hoursOldMax", row.hoursOldMax, "number")}</TableCell>}
                                    {showAll && (
                                        <TableCell>
                                            <MuiLink href={`/events/${row.hpEventId}`} target="_blank" rel="noreferrer">{row.hpEventId}</MuiLink>
                                        </TableCell>
                                    )}
                                    <TableCell sx={{bgcolor: row.pmActive ? undefined : "#f2dede"}}>
                                        {canPm
                                            ? <MuiLink href={`/programSets/${row.hpCampusSearchId}`} target="_blank" rel="noreferrer">{row.hpCampusSearchId}</MuiLink>
                                            : row.hpCampusSearchId}
                                    </TableCell>
                                    <TableCell align="right">{editable(row, "costPerLead", money(row.costPerLead), "number")}</TableCell>
                                    <TableCell align="right">{num(row.dailySent)}</TableCell>
                                    <TableCell align="right">{editable(row, "dailyCap", num(row.dailyCap), "number")}</TableCell>
                                    <TableCell align="right">{num(row.monthlySent)}</TableCell>
                                    <TableCell align="right">
                                        {editable(row, "monthlyCap", (
                                            <MuiLink href={`/campaignConfirms/${row.hpCampaignId}`} target="_blank" rel="noreferrer">{num(row.monthlyCap)}</MuiLink>
                                        ), "number")}
                                    </TableCell>
                                    <TableCell align="right" sx={{bgcolor: row.rangeGood > 0 ? "#dff0d8" : undefined}}>
                                        {row.rangeGood > 0 ? num(row.rangeGood) : ""}
                                    </TableCell>
                                    <TableCell align="right" sx={{bgcolor: row.rangeUnknown > 0 ? "#fcf8e3" : undefined}}>
                                        {row.rangeUnknown > 0 ? num(row.rangeUnknown) : ""}
                                    </TableCell>
                                    <TableCell align="right" sx={{bgcolor: row.rangeBad > 0 ? "#f2dede" : undefined}}>
                                        {row.rangeBad > 0 ? num(row.rangeBad) : ""}
                                    </TableCell>
                                </StripedTableRow>
                        ))}
                    </TableBody>
                    {!loading && !!rows.length && (
                        <TableFooter>
                            <TableFooterRow>
                                <TableCell/>
                                <TableCell/>
                                <TableCell/>
                                <TableCell/>
                                <TableCell/>
                                <TableCell/>
                                {showAll && <TableCell/>}
                                {showAll && <TableCell/>}
                                {showAll && <TableCell/>}
                                {showAll && <TableCell/>}
                                <TableCell/>
                                <TableCell/>
                                <TableCell align="right">{num(totals.dailySent)}</TableCell>
                                <TableCell align="right">{num(totals.dailyCap)}</TableCell>
                                <TableCell align="right">{num(totals.monthlySent)}</TableCell>
                                <TableCell align="right">{num(totals.monthlyCap)}</TableCell>
                                <TableCell align="right">{num(totals.rangeGood)}</TableCell>
                                <TableCell align="right">{num(totals.rangeUnknown)}</TableCell>
                                <TableCell align="right">{num(totals.rangeBad)}</TableCell>
                            </TableFooterRow>
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>
        </>
    );
}
